import type { ReactNode } from 'react';

import { cx } from '@/lib/cx';

import styles from './Page.module.css';

/**
 * Layout primitives every screen is built from.
 *
 * Kept here rather than repeated per feature, so page padding, heading sizes and panel
 * chrome cannot drift screen by screen.
 */

export function Page({ children }: { children: ReactNode }) {
  return <div className={styles.page}>{children}</div>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.header}>
      <div className={styles.heading}>
        {/* The only h1 on the page: the breadcrumb above is navigation, not a
            heading, so this is where a screen reader's document outline starts. */}
        <h1 className={styles.title}>{title}</h1>
        {description ? <p className={styles.description}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}

export function Panel({
  title,
  aside,
  icon,
  children,
  flush = false,
}: {
  title?: string;
  aside?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  /** No body padding, for a panel whose whole content is a table. */
  flush?: boolean;
}) {
  return (
    <section className={styles.panel}>
      {title ? (
        <div className={styles.panelHead}>
          {icon}
          <h2 className={styles.panelTitle}>{title}</h2>
          {aside ? <span className={styles.panelAside}>{aside}</span> : null}
        </div>
      ) : null}
      <div className={cx(styles.panelBody, flush && styles.panelFlush)}>{children}</div>
    </section>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className={styles.toolbar}>{children}</div>;
}
